/**
 * TUVI - Recepción del material de la Ficha FFE (arriba de la Ficha FFE del proyecto)
 *
 * Solo el usuario vinculado al residente asignado puede aceptar el recibo: todo,
 * o "con faltantes" describiendo qué faltó. Todos ven quién aceptó y cuándo, y
 * un aviso si la ficha cambió después. Con permiso se puede anular la aceptación
 * (queda en el historial) para que el residente vuelva a aceptar.
 */
import React, { useState, useEffect, useCallback } from 'react';
import client from '../../../services/graphqlClient';
import { useNotification } from '../../../context/NotificationContext';
import './RecepcionFfe.css';

const fechaHora = (iso) => (iso
  ? new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');
const pesos = (v) => '$ ' + Math.round(Number(v || 0)).toLocaleString('es-CO');

const CAMPOS = `id estado faltantes residente_nombre usuario_nombre aceptado_at total_actividades
  valor_presupuesto anulado_por_nombre anulado_at motivo_anulacion`;

export default function RecepcionFfe({ proyectoId }) {
  const { success, error } = useNotification();
  const [estado, setEstado] = useState(null);
  const [marcado, setMarcado] = useState(false);
  const [conFaltantes, setConFaltantes] = useState(false);
  const [faltantes, setFaltantes] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [verCambios, setVerCambios] = useState(false);
  const [verHistorial, setVerHistorial] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const d = await client.query(`query($pid: Int!) { recepcionFfeProyecto(proyecto_id: $pid) {
        tiene_ficha residente_nombre residente_registrado residente_usuario_nombre puede_aceptar puede_anular
        recepcion { ${CAMPOS} }
        cambios { hay_cambios nuevas quitadas cambiadas total_actividades_actual valor_presupuesto_actual detalle }
        anuladas { ${CAMPOS} }
      } }`, { pid: proyectoId });
      setEstado(d.recepcionFfeProyecto);
    } catch (e) { console.error('Error cargando recepción FFE:', e); }
  }, [proyectoId]);

  useEffect(() => { cargar(); }, [cargar]);

  const aceptar = async (faltaron) => {
    const texto = faltantes.trim();
    if (faltaron && !texto) { error('Describe qué material faltó'); return; }
    const pregunta = faltaron
      ? '¿Registrar el recibo del material CON FALTANTES?\n\nQuedará guardado con la fecha y la hora de ahora y no se puede deshacer.'
      : '¿Confirmas que recibiste TODO el material de la Ficha FFE?\n\nQuedará guardado con la fecha y la hora de ahora y no se puede deshacer.';
    if (!window.confirm(pregunta)) { setMarcado(false); return; }
    setGuardando(true);
    try {
      const r = await client.mutate(
        `mutation($pid: Int!, $cf: Boolean, $f: String) { aceptarRecepcionFfe(proyecto_id: $pid, con_faltantes: $cf, faltantes: $f) { success message } }`,
        { pid: proyectoId, cf: !!faltaron, f: faltaron ? texto : null }
      );
      if (r.aceptarRecepcionFfe.success) {
        success(r.aceptarRecepcionFfe.message);
        setConFaltantes(false); setFaltantes('');
      } else error(r.aceptarRecepcionFfe.message);
    } catch (e) { error('No se pudo registrar: ' + (e.message || '')); }
    finally { setGuardando(false); setMarcado(false); cargar(); }
  };

  const anular = async () => {
    const motivo = window.prompt('¿Por qué se anula la aceptación? (obligatorio)\n\nEl residente tendrá que volver a aceptar el recibo del material.');
    if (motivo === null) return;
    if (!motivo.trim()) { error('El motivo es obligatorio'); return; }
    try {
      const r = await client.mutate(
        `mutation($pid: Int!, $m: String!) { anularRecepcionFfe(proyecto_id: $pid, motivo: $m) { success message } }`,
        { pid: proyectoId, m: motivo.trim() }
      );
      if (r.anularRecepcionFfe.success) success(r.anularRecepcionFfe.message);
      else error(r.anularRecepcionFfe.message);
    } catch (e) { error('No se pudo anular: ' + (e.message || '')); }
    finally { cargar(); }
  };

  if (!estado || !estado.tiene_ficha) return null;
  const { recepcion: rec, cambios } = estado;

  const historial = estado.anuladas.length > 0 && (
    <div className="rf-historial">
      <button className="rf-link" onClick={() => setVerHistorial(v => !v)}>
        {verHistorial ? 'Ocultar' : 'Ver'} aceptaciones anuladas ({estado.anuladas.length})
      </button>
      {verHistorial && (
        <ul>
          {estado.anuladas.map(a => (
            <li key={a.id}>
              {a.estado === 'con_faltantes' ? 'Recibido con faltantes' : 'Aceptado'} por <strong>{a.residente_nombre}</strong> el {fechaHora(a.aceptado_at)}
              {' · '}anulado por <strong>{a.anulado_por_nombre}</strong> el {fechaHora(a.anulado_at)}: <em>{a.motivo_anulacion}</em>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // ---------- Ya aceptada ----------
  if (rec) {
    const faltaron = rec.estado === 'con_faltantes';
    const quien = rec.residente_nombre + (rec.usuario_nombre && rec.usuario_nombre !== rec.residente_nombre ? ` (${rec.usuario_nombre})` : '');
    return (
      <div className={`rf-panel ${faltaron ? 'rf-faltantes' : 'rf-aceptado'}`}>
        <div className="rf-fila">
          <div className="rf-icono">{faltaron ? '⚠' : '✓'}</div>
          <div className="rf-cuerpo">
            <div className="rf-titulo">{faltaron ? 'Material recibido con faltantes' : 'Material de la Ficha FFE recibido'}</div>
            <div className="rf-texto">
              {faltaron ? 'Registrado' : 'Aceptado'} por <strong>{quien}</strong> el <strong>{fechaHora(rec.aceptado_at)}</strong>
            </div>
            <div className="rf-sub">
              Ficha al aceptarla: {rec.total_actividades} actividades · presupuesto {pesos(rec.valor_presupuesto)}
            </div>
            {faltaron && <div className="rf-faltantes-texto"><strong>Faltó:</strong> {rec.faltantes}</div>}
          </div>
          {estado.puede_anular && (
            <button className="rf-btn rf-btn-sec" onClick={anular}>Anular aceptación</button>
          )}
        </div>

        {cambios?.hay_cambios && (
          <div className="rf-cambios">
            <strong>La ficha cambió después de la aceptación:</strong>{' '}
            {[
              cambios.nuevas ? `${cambios.nuevas} actividad(es) nueva(s)` : null,
              cambios.quitadas ? `${cambios.quitadas} quitada(s)` : null,
              cambios.cambiadas ? `${cambios.cambiadas} con otra cantidad` : null,
              Math.abs((cambios.valor_presupuesto_actual || 0) - (rec.valor_presupuesto || 0)) >= 1
                ? `presupuesto ${pesos(rec.valor_presupuesto)} → ${pesos(cambios.valor_presupuesto_actual)}` : null,
            ].filter(Boolean).join(' · ')}
            {cambios.detalle?.length > 0 && (
              <>
                {' '}<button className="rf-link" onClick={() => setVerCambios(v => !v)}>{verCambios ? 'Ocultar' : 'Ver cambios'}</button>
                {verCambios && <ul>{cambios.detalle.map((d, i) => <li key={i}>{d}</li>)}</ul>}
              </>
            )}
          </div>
        )}
        {historial}
      </div>
    );
  }

  // ---------- Pendiente: el residente puede aceptar ----------
  if (estado.puede_aceptar) {
    return (
      <div className="rf-panel rf-accion">
        <div className="rf-titulo">Recibo del material de la Ficha FFE</div>
        <div className="rf-texto">
          Revisa que te hayan entregado todo el material de esta ficha. Al aceptar queda guardada la fecha y la hora.
        </div>
        {!conFaltantes ? (
          <div className="rf-acciones">
            <label className="rf-check">
              <input
                type="checkbox"
                checked={marcado}
                disabled={guardando}
                onChange={(e) => { setMarcado(e.target.checked); if (e.target.checked) aceptar(false); }}
              />
              Confirmo que recibí todo el material de la Ficha FFE
            </label>
            <button className="rf-link" onClick={() => setConFaltantes(true)} disabled={guardando}>
              Recibí con faltantes
            </button>
          </div>
        ) : (
          <div className="rf-faltantes-form">
            <textarea
              value={faltantes}
              onChange={(e) => setFaltantes(e.target.value)}
              placeholder="¿Qué material faltó? (obligatorio)"
              rows={3}
              autoFocus
            />
            <div className="rf-acciones">
              <button className="rf-btn" onClick={() => aceptar(true)} disabled={guardando || !faltantes.trim()}>
                {guardando ? 'Guardando...' : 'Registrar recibo con faltantes'}
              </button>
              <button className="rf-link" onClick={() => { setConFaltantes(false); setFaltantes(''); }} disabled={guardando}>
                Cancelar
              </button>
            </div>
          </div>
        )}
        {historial}
      </div>
    );
  }

  // ---------- Pendiente: lo ven los demás ----------
  let aviso;
  if (!estado.residente_nombre) {
    aviso = 'Pendiente: asigna el residente en Información del proyecto. Él aceptará el recibo del material.';
  } else if (!estado.residente_registrado) {
    aviso = <>El residente <strong>{estado.residente_nombre}</strong> no está registrado en Administrativo → Residentes. Regístralo y vincúlale su usuario para que pueda aceptar el recibo del material.</>;
  } else if (!estado.residente_usuario_nombre) {
    aviso = <>El residente <strong>{estado.residente_nombre}</strong> no tiene usuario del sistema vinculado. Vincúlalo en Administrativo → Residentes para que pueda aceptar el recibo del material.</>;
  } else {
    aviso = <>Pendiente de aceptación del recibo del material por <strong>{estado.residente_nombre}</strong>.</>;
  }
  return (
    <div className="rf-panel rf-pendiente">
      <div className="rf-fila">
        <div className="rf-icono">⏳</div>
        <div className="rf-cuerpo">
          <div className="rf-titulo">Recibo del material de la Ficha FFE</div>
          <div className="rf-texto">{aviso}</div>
        </div>
      </div>
      {historial}
    </div>
  );
}
